"use client"
import { aboutSectionData_en } from "@/data/en/about-section/v1";
import { aboutSectionData_ar } from "@/data/ar/about-section/v1";
import { ImageProps } from "@/src/common-types";
import { Container } from "@/src/components/container";
import { SectionHeading } from "@/src/components/section-heading";
import { SectionHeadingWithoutStylingProps } from "@/src/components/section-heading/interface";
import Image from "next/image";
import { useEffect, useState } from "react";

import patternOne from "public/assets/images/about/pattern-1.png";
import justbrand2small from "public/assets/images/brand/justbrand2small.png";

export interface AboutSectionProps {
  images: {
    image1: ImageProps;
    image2: ImageProps;
    image3: ImageProps;
    image4: ImageProps;
  };
  sectionHeading: SectionHeadingWithoutStylingProps;
  keyPoints: {
    icon: React.ReactNode;
    title: string;
    img: string;
  }[];
  description?: string;
  params: { lang: string };
}

export function AboutSection({ params }: AboutSectionProps) {
  const [isMounted, setIsMounted] = useState(false);
  const isArabic = params.lang === "ar";
  const { images, sectionHeading, keyPoints, description } = isArabic ? aboutSectionData_ar : aboutSectionData_en;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <section className="section-padding-primary overflow-hidden">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-[.92fr_1fr] 2xl:gap-20">
          {/* Content  */}
          <div className="lg:order-2" data-aos="fade-left" data-aos-delay="200">
            <SectionHeading params={params} {...sectionHeading} />
            {keyPoints && keyPoints.length > 0 && (
              <div className="mt-7 grid gap-5 text-accent-900 dark:text-white sm:grid-cols-2 md:mt-10 md:gap-[1.875rem]">
                <div key={"1"} className="mt-0 w-full flex justify-start gap-4">
                  {keyPoints.map((point, index) => (
                    <a key={index} href="#" className="w-40 h-full" aria-label="image">
                      <Image
                        className="w-full hover:cursor-pointer"
                        src={point.img}
                        alt={point.title}
                        width={193}
                        height={67}
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {description && <p className="mt-5 md:mt-[1.875rem]">{description}</p>}
          </div>

          {/* Images  */}
          {images && isMounted && (
            <div className="mx-auto text-center">
              <div className="relative mx-auto grid max-w-[580px] grid-cols-2 gap-2.5 overflow-hidden">
                {/* Image 1  */}
                <div className="relative flex justify-end overflow-hidden">
                  <div className="relative z-[2] mt-auto" data-aos="fade-up" data-aos-delay="200">
                    <span className="absolute -left-2.5 -top-2.5 -z-1 h-full w-full rounded-tl-[60px] bg-primary-light"></span>
                    <div className="relative z-[4] overflow-hidden">
                      <Image
                        src={images.image1.src}
                        alt={images.image1.alt}
                        width={images.image1.width}
                        height={images.image1.height}
                        className="rounded-tl-[60px] object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Pattern  */}
                  <Image
                    src={patternOne.src}
                    width={383}
                    height={246}
                    alt="pattern-1"
                    placeholder="blur"
                    blurDataURL={patternOne.blurDataURL}
                    className="absolute bottom-[-55%] left-[42px] z-1 block object-cover"
                    data-aos="zoom-in"
                    data-aos-delay="250"
                  />
                </div>

                {/* Image 2  */}
                <div className="flex overflow-hidden">
                  <Image
                    src={images.image2.src}
                    alt={images.image2.alt}
                    width={images.image2.width}
                    height={images.image2.height}
                    className="rounded-[80px_10px] object-cover"
                    data-aos="fade-right"
                    data-aos-delay="300"
                  />
                </div>

                {/* Image 3  */}
                <div className="ml-auto flex max-w-[250px] justify-end overflow-hidden">
                  <Image
                    src={images.image3.src}
                    alt={images.image3.alt}
                    width={images.image3.width}
                    height={images.image3.height}
                    className="rounded-[50px_10px] object-cover"
                    data-aos="fade-left"
                    data-aos-delay="350"
                  />
                </div>

                {/* Image 4 */}
                <div className="overflow-hidden">
                  <Image
                    src={images.image4.src}
                    alt={images.image4.alt}
                    width={images.image4.width}
                    height={images.image4.height}
                    className="rounded-10 rounded-br-[80px] object-cover"
                    data-aos="fade-right"
                    data-aos-delay="400"
                  />
                </div>

                <div className="absolute left-1/2 top-1/2 z-[4] -translate-x-1/2 -translate-y-1/2 overflow-hidden">
                  <div data-aos="zoom-in" data-aos-delay="450">
                    <span className="grid h-[100px] w-[100px] place-items-center rounded-full border-[12px] border-white bg-primary text-[30px] text-white">
                      <Image
                        src={justbrand2small.src}
                        alt="justbrand2small"
                        width={60}
                        height={40}
                        className="rounded-full"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
}
